'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { EmpresaService, PedidosService, TermosCondicoesService, SubscriptionService, cnpjService } from '../../lib/services'
import { getLocalStorageInDev, setLocalStorage, clearIfCnpjChanged, clearPedidoAndColaboradoresData } from '../../lib/utils/localStorage'
import { FormMessage } from '../../lib/components'
import { formatCNPJ, formatCPF, formatTelefone, cleanCNPJ, cleanCPF, cleanTelefone } from '../../lib/utils/formatters'
import { isValidCNPJ, isValidCPF } from '../../lib/utils/validators'
import { trackingService } from '../../lib/services/tracking.service'
import RecoveryModal from './RecoveryModal'
import TermosCondicoesModal from './TermosCondicoesModal'
import SubscriptionWarningModal from './SubscriptionWarningModal'
import type { TermosCondicoes } from '../../lib/services/termos-condicoes.service'

interface CompanyFormProps {
  onSubmit?: (data: any) => void
  onSuccess?: (data: any) => void
  initialData?: any
  isModal?: boolean
}

export default function CompanyForm({ onSubmit, onSuccess, initialData, isModal = false }: CompanyFormProps) {
  const router = useRouter()
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cnpjError, setCnpjError] = useState('')
  const [cpfError, setCpfError] = useState('')
  const [showRecoveryModal, setShowRecoveryModal] = useState(false)
  const [pendingOrder, setPendingOrder] = useState<any>(null)
  const [termosAceitos, setTermosAceitos] = useState(false)
  const [termosLidos, setTermosLidos] = useState(false)
  const [showTermosModal, setShowTermosModal] = useState(false)
  const [termosCondicoes, setTermosCondicoes] = useState<TermosCondicoes | null>(null)
  const [loadingTermos, setLoadingTermos] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [activeSubscription, setActiveSubscription] = useState<any>(null)
  const [loadingCnpj, setLoadingCnpj] = useState(false)
  const [hasSimplesNacionalFromApi, setHasSimplesNacionalFromApi] = useState(false)
  const [formData, setFormData] = useState({
    ocupation: 'PJ',
    cnpj: '',
    cpf: '',
    nomeEmpresa: '',
    nomeEmpregador: '',
    email: '',
    telefone: '',
    pcmso: '',
    nomeMedico: '',
    crmMedico: '',
    ufCRM: '',
    optante_simples_nacional: false,
  })

  // Carregar dados salvos do localStorage ao montar (apenas em desenvolvimento)
  // IMPORTANTE: Não carregar dados se o CNPJ mudou (clearIfCnpjChanged já limpou)
  useEffect(() => {
    const savedData = getLocalStorageInDev('companyData')
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        // Verificar se o CNPJ/CPF do savedData corresponde ao último usado
        const lastCnpj = localStorage.getItem('lastUsedCnpj')
        const savedCnpj = parsedData?.cnpj || parsedData?.cpf || ''
        const cleanedSavedCnpj = savedCnpj ? savedCnpj.replace(/\D/g, '') : null

        // Só carregar se o CNPJ não mudou ou se não há último CNPJ salvo
        if (!lastCnpj || !cleanedSavedCnpj || lastCnpj === cleanedSavedCnpj) {
          setFormData(prev => ({ ...prev, ...parsedData }))
          console.log('✅ CompanyData carregado do localStorage (CNPJ corresponde)')
        } else {
          console.log('⚠️ CompanyData não carregado - CNPJ mudou desde o último uso')
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error)
      }
    }
  }, [])

  // Salvar automaticamente no localStorage sempre que formData mudar
  useEffect(() => {
    if (formData.nomeEmpresa || formData.email || formData.telefone) {
      localStorage.setItem('companyData', JSON.stringify(formData))
    }
  }, [formData])

  // Buscar termos e condições ativos ao montar
  useEffect(() => {
    const loadTermos = async () => {
      try {
        setLoadingTermos(true)
        const termo = await TermosCondicoesService.getAtivo('AGENDAMENTO')
        setTermosCondicoes(termo)
      } catch (error) {
        console.error('❌ Erro ao carregar termos e condições:', error)
      } finally {
        setLoadingTermos(false)
      }
    }

    loadTermos()
  }, [])

  // Effect to open modal automatically removed


  const handleToggleTermos = (checked: boolean) => {
    setTermosAceitos(checked)
    // Removed: if (!checked) { setTermosLidos(false) } - No longer required to read
    setTermosLidos(true) // Auto-mark as read when toggled
  }

  const handleOpenTermosModal = () => {
    if (termosCondicoes) {
      setShowTermosModal(true)
    }
  }

  const handleCloseTermosModal = () => {
    setShowTermosModal(false)
  }

  const handleTermosReadComplete = () => {
    setTermosLidos(true)
  }

  const handleOcupationChange = (value: string) => {
    setFormData(prev => ({ ...prev, ocupation: value }))
    // Limpar erros ao trocar entre PJ e PF
    setCnpjError('')
    setCpfError('')
    setError('')
  }

  const handlePCMSOChange = (value: string) => {
    setFormData(prev => ({ ...prev, pcmso: value }))
  }

  // Buscar dados do CNPJ na BrasilAPI
  useEffect(() => {
    const fetchCnpjData = async () => {
      if (formData.ocupation !== 'PJ') {
        setHasSimplesNacionalFromApi(false)
        return;
      }

      const cnpjLimp = formData.cnpj.replace(/\D/g, '')
      if (cnpjLimp.length === 14) {
        if (!isValidCNPJ(cnpjLimp)) return;

        try {
          setLoadingCnpj(true)
          const data = await cnpjService.buscarCnpj(cnpjLimp)
          if (data) {
            // Tentar extrair o nome de um contato/sócio (priorizar pessoas físicas)
            let nomeContato = '';
            if (data.qsa && data.qsa.length > 0) {
              const socioPF = data.qsa.find((s: any) =>
                s.qualificacao_socio?.toLowerCase().includes('administrador') ||
                s.qualificacao_socio?.toLowerCase().includes('diretor') ||
                s.qualificacao_socio?.toLowerCase().includes('presidente') ||
                (s.nome_socio && !s.nome_socio.match(/(LTDA|S\.A\.|S\/A|LLC|INC|LTD|COMPANY|PARTICIPACOES|EMPREENDIMENTOS|ASSOCIACAO)/i))
              );
              nomeContato = socioPF ? socioPF.nome_socio : data.qsa[0].nome_socio;
            } else {
              nomeContato = data.nome_fantasia || '';
            }

            setFormData(prev => ({
              ...prev,
              nomeEmpresa: data.razao_social || '',
              nomeEmpregador: nomeContato || '',
              email: data.email || '',
              telefone: (data.ddd_telefone_1 || data.ddd_telefone_2)
                ? formatTelefone(data.ddd_telefone_1 || data.ddd_telefone_2)
                : (data.telefone ? formatTelefone(data.telefone) : ''),
              optante_simples_nacional: data.opcao_pelo_simples ?? false,
            }))

            if (typeof data.opcao_pelo_simples === 'boolean') {
              setHasSimplesNacionalFromApi(true)
            } else {
              setHasSimplesNacionalFromApi(false)
            }

            if (!data.email) {
              // Focar no campo de e-mail logo após liberar o input
              setTimeout(() => {
                emailInputRef.current?.focus()
              }, 100)
            }
          } else {
            setHasSimplesNacionalFromApi(false)
          }
        } catch (error) {
          console.warn('Erro ao buscar dados do CNPJ com fallback:', error)
          setHasSimplesNacionalFromApi(false)
        } finally {
          setLoadingCnpj(false)
        }
      } else {
        setHasSimplesNacionalFromApi(false)
      }
    }

    fetchCnpjData()
  }, [formData.cnpj, formData.ocupation])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    let formattedValue = value;

    // Aplicar formatação baseada no campo
    if (name === 'cnpj') {
      formattedValue = formatCNPJ(value);
      // Validar CNPJ em tempo real quando o campo estiver completo
      if (formattedValue.replace(/\D/g, '').length === 14) {
        const cleanedCnpj = cleanCNPJ(formattedValue)
        if (!isValidCNPJ(cleanedCnpj)) {
          setCnpjError('CNPJ inválido. Verifique os dígitos informados.')
        } else {
          setCnpjError('')
          // Verificar se CNPJ mudou e limpar localStorage se necessário
          clearIfCnpjChanged(cleanedCnpj)
        }
      } else {
        setCnpjError('')
      }
    } else if (name === 'cpf') {
      formattedValue = formatCPF(value);
      // Validar CPF em tempo real quando o campo estiver completo
      if (formattedValue.replace(/\D/g, '').length === 11) {
        const cleanedCpf = cleanCPF(formattedValue)
        if (!isValidCPF(cleanedCpf)) {
          setCpfError('CPF inválido. Verifique os dígitos informados.')
        } else {
          setCpfError('')
          // Verificar se CPF mudou e limpar localStorage se necessário
          clearIfCnpjChanged(cleanedCpf)
        }
      } else {
        setCpfError('')
      }
    } else if (name === 'telefone') {
      formattedValue = formatTelefone(value);
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))
    // Limpar erro geral quando o usuário começar a digitar novamente
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    // Salvar URL de origem explicitamente para navegação segura de "Voltar" no funil
    if (typeof window !== 'undefined') {
      localStorage.setItem('cadastroOriginUrl', window.location.pathname + window.location.search)
    }

    // Validar aceite dos termos
    // Validar aceite dos termos
    if (termosCondicoes && !termosAceitos) {
      setError('Você precisa aceitar os Termos e Condições de Serviço para continuar.')
      setLoading(false)
      return
    }


    try {
      // Validação matemática de CNPJ/CPF antes de enviar
      let documentoLimpo = '';
      if (formData.ocupation === 'PJ') {
        const cleanedCnpj = cleanCNPJ(formData.cnpj)
        if (!cleanedCnpj) {
          setCnpjError('CNPJ é obrigatório')
          setError('CNPJ é obrigatório')
          setLoading(false)
          return
        }
        if (!isValidCNPJ(cleanedCnpj)) {
          setCnpjError('CNPJ inválido. Verifique os dígitos informados.')
          setError('CNPJ inválido. Verifique os dígitos informados.')
          setLoading(false)
          return
        }
        setCnpjError('') // Limpar erro se válido
        documentoLimpo = cleanedCnpj;
      } else {
        const cleanedCpf = cleanCPF(formData.cpf || '')
        if (!cleanedCpf) {
          setCpfError('CPF é obrigatório')
          setError('CPF é obrigatório')
          setLoading(false)
          return
        }
        if (!isValidCPF(cleanedCpf)) {
          setCpfError('CPF inválido. Verifique os dígitos informados.')
          setError('CPF inválido. Verifique os dígitos informados.')
          setLoading(false)
          return
        }
        setCpfError('') // Limpar erro se válido
        documentoLimpo = cleanedCpf;
      }

      // Verificar se CNPJ mudou e limpar localStorage se necessário (fallback)
      clearIfCnpjChanged(documentoLimpo);

      // Validar email obrigatório
      if (!formData.email || formData.email.trim() === '') {
        setError('Email é obrigatório para continuar')
        setLoading(false)
        return
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        setError('Email inválido')
        setLoading(false)
        return
      }

      // Preparar dados para a API (mapear campos do form para DTO)
      // ENDEREÇO NÃO É SOLICITADO NO CADASTRO INICIAL - VEM NO PAGAMENTO
      // Limpar formatação de campos numéricos antes de enviar
      const empresaData = {
        razao_social: formData.nomeEmpresa,
        cnpj: formData.ocupation === 'PJ' ? cleanCNPJ(formData.cnpj) : cleanCPF(formData.cpf || ''),
        nome_fantasia: formData.nomeEmpresa, // Usando mesmo valor por enquanto
        telefone: cleanTelefone(formData.telefone),
        email: formData.email,
        pessoa: formData.ocupation === 'PF' ? 1 : 0, // 1 = PF, 0 = PJ
        optante_simples_nacional: formData.ocupation === 'PJ' ? formData.optante_simples_nacional : false,
        // endereco: NÃO ENVIAR - será solicitado no pagamento
        // Incluir dados de PCMSO se informado
        ...(formData.pcmso === '1' && formData.nomeMedico && formData.crmMedico && formData.ufCRM && {
          pcmso: formData.pcmso,
          nomeMedico: formData.nomeMedico,
          crmMedico: formData.crmMedico,
          ufCRM: formData.ufCRM,
        }),
      }

      // Chamar API para criar/atualizar empresa
      let response;
      try {
        response = await EmpresaService.createEmpresa(empresaData)
      } catch (error: any) {
        console.error('❌ [CompanyForm] Erro ao criar/atualizar empresa na API:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao salvar dados da empresa. Por favor, tente novamente.'
        setError(errorMessage)
        setLoading(false)
        return
      }

      // Validar que a resposta da API contém os dados necessários
      if (!response || !response.id) {
        console.error('❌ [CompanyForm] ERRO CRÍTICO: Resposta da API inválida ou sem ID da empresa')
        setError('Erro: Dados da empresa não foram retornados corretamente. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      console.log('✅ [CompanyForm] Empresa criada/atualizada com sucesso:', response)
      console.log('✅ [CompanyForm] Solicitante ID:', response.solicitante_id)
      console.log('✅ [CompanyForm] Tokens salvos em cookies HttpOnly (access_token + refresh_token)')

      // Tokens são gerenciados via cookies HttpOnly - não aparecem na resposta
      // Se a requisição foi bem-sucedida, assumimos que os cookies foram definidos

      // Track user registered event
      if (response.id) {
        trackingService.trackUserRegistered(response.id, {
          cnpj: empresaData.cnpj,
          razao_social: empresaData.razao_social,
        });
      }

      // Salvar dados da empresa no localStorage (SOBRESCREVER sempre)
      // Tokens são gerenciados exclusivamente via cookies HttpOnly
      try {
        localStorage.setItem('empresaData', JSON.stringify(response))
        console.log('✅ [CompanyForm] empresaData salvo no localStorage')
      } catch (error) {
        console.error('❌ [CompanyForm] Erro ao salvar empresaData no localStorage:', error)
        setError('Erro ao salvar dados da empresa. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      // Validar e salvar solicitante_id (OBRIGATÓRIO)
      if (!response.solicitante_id) {
        console.error('❌ [CompanyForm] ERRO CRÍTICO: solicitante_id não retornado pela API')
        setError('Erro: Não foi possível criar o solicitante. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      // Salvar solicitante_id (SOBRESCREVER sempre)
      try {
        localStorage.setItem('solicitanteId', String(response.solicitante_id))
        console.log('✅ [CompanyForm] Solicitante ID salvo no localStorage:', response.solicitante_id)
      } catch (error) {
        console.error('❌ [CompanyForm] Erro ao salvar solicitanteId no localStorage:', error)
        setError('Erro ao salvar dados do solicitante. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      // Validar que os dados foram salvos corretamente
      const empresaDataSalvo = localStorage.getItem('empresaData')
      const solicitanteIdSalvo = localStorage.getItem('solicitanteId')

      if (!empresaDataSalvo || !solicitanteIdSalvo) {
        console.error('❌ [CompanyForm] ERRO CRÍTICO: Dados não foram salvos corretamente no localStorage')
        setError('Erro ao salvar dados. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      console.log('✅ [CompanyForm] Dados da empresa e solicitante salvos com sucesso no localStorage')

      // Verificar se existe assinatura ativa por CNPJ antes de continuar
      try {
        console.log('🔍 Verificando assinatura ativa por CNPJ...')
        const subscription = await SubscriptionService.checkActiveSubscriptionByCnpj(documentoLimpo)

        if (subscription) {
          console.log('✅ Assinatura ativa encontrada:', subscription)
          setActiveSubscription(subscription)
          setShowSubscriptionModal(true)
          setLoading(false)
          return // Não redirecionar ainda, aguardar decisão do usuário
        } else {
          console.log('ℹ️ Nenhuma assinatura ativa encontrada, continuando fluxo normal')
        }
      } catch (error: any) {
        console.error('⚠️ Erro ao verificar assinatura:', error)
        // Não bloquear o fluxo se houver erro ao verificar assinatura
      }

      // Limpar colaboradores de outras empresas imediatamente
      try {
        const savedEmployees = localStorage.getItem('employeesData')
        if (savedEmployees) {
          const employees = JSON.parse(savedEmployees)
          const empresaId = response.id
          const empresaCnpj = response.cnpj || (response as any).cpf

          const employeesFiltered = employees.filter((emp: any) => {
            if (emp.empresa_id) {
              return emp.empresa_id === empresaId
            }
            if (emp.empresa_cnpj) {
              return emp.empresa_cnpj === empresaCnpj
            }
            return false // Excluir sem vinculação
          })

          if (employees.length !== employeesFiltered.length) {
            localStorage.setItem('employeesData', JSON.stringify(employeesFiltered))
            console.log(`🧹 Removidos ${employees.length - employeesFiltered.length} colaboradores de outras empresas`)
          }
        }
      } catch (error) {
        console.warn('⚠️ Erro ao limpar colaboradores:', error)
      }

      // Verificar se há pedido pendente após login
      try {
        console.log('🔍 [CompanyForm] Verificando pedido pendente...')
        const pendingPedido = await PedidosService.getPendingPedido(response.id)

        if (pendingPedido) {
          console.log('📋 [CompanyForm] Pedido pendente encontrado:', pendingPedido)

          // Validar critérios para retomar pedido
          const pedidoCnpj = pendingPedido.empresa?.cnpj || ''
          const pedidoEmail = pendingPedido.empresa_solicitante?.email || ''
          const empresaCnpj = response.cnpj || ''
          const empresaEmail = formData.email || ''

          // Normalizar CNPJs para comparação (remover formatação)
          const pedidoCnpjNormalizado = pedidoCnpj.replace(/\D/g, '')
          const empresaCnpjNormalizado = empresaCnpj.replace(/\D/g, '')

          // Normalizar emails para comparação (lowercase, trim)
          const pedidoEmailNormalizado = pedidoEmail.toLowerCase().trim()
          const empresaEmailNormalizado = empresaEmail.toLowerCase().trim()

          // Validar CNPJ (sempre obrigatório)
          const cnpjMatch = pedidoCnpjNormalizado === empresaCnpjNormalizado;

          // Validar email (sempre obrigatório se solicitante existe)
          // Se empresa_solicitante é null, pedido não pode ser retomado
          if (!pendingPedido.empresa_solicitante) {
            console.log('⚠️ [CompanyForm] Pedido não tem empresa_solicitante. Não pode ser retomado.');
            clearPedidoAndColaboradoresData();
            // Não fazer return - deixar fluxo continuar normalmente para redirecionar
          }

          const emailMatch = pedidoEmailNormalizado === empresaEmailNormalizado;

          console.log('🔍 [CompanyForm] Validando critérios para retomar pedido:', {
            pedidoCnpj: pedidoCnpjNormalizado,
            empresaCnpj: empresaCnpjNormalizado,
            cnpjMatch,
            pedidoEmail: pedidoEmailNormalizado,
            empresaEmail: empresaEmailNormalizado,
            emailMatch,
            temSolicitante: !!pendingPedido.empresa_solicitante,
          });

          if (cnpjMatch && emailMatch) {
            console.log('✅ [CompanyForm] Critérios atendidos - mostrando modal para retomar pedido')
            setPendingOrder(pendingPedido)
            setShowRecoveryModal(true)
            return // Não redirecionar ainda, aguardar decisão do usuário
          } else {
            console.log('⚠️ [CompanyForm] Critérios não atendidos (CNPJ ou email diferentes) - limpando dados e criando novo pedido')
            // Limpar dados de pedido, colaboradores e agendamento
            clearPedidoAndColaboradoresData()
          }
        } else {
          console.log('✅ [CompanyForm] Nenhum pedido pendente encontrado')
          // Limpar dados de pedido, colaboradores e agendamento para garantir estado limpo
          clearPedidoAndColaboradoresData()
        }
      } catch (error: any) {
        console.error('⚠️ [CompanyForm] Erro ao verificar pedido pendente:', error)
        // Não bloquear o fluxo se houver erro ao verificar pedido pendente
        // Limpar dados para garantir estado limpo
        clearPedidoAndColaboradoresData()
      }

      // Continuar com o fluxo normal (redirecionar)
      handleContinueAfterSubscriptionCheck(response)
    } catch (error: any) {
      // Log detalhado do erro para debug
      console.error('❌ Erro ao criar empresa:', error)
      console.error('❌ Detalhes do erro:', {
        message: error?.message,
        response: error?.response,
        responseData: error?.response?.data,
        status: error?.response?.status,
        code: error?.code,
        name: error?.name,
      })

      // Melhor tratamento de erro para diferentes formatos de resposta da API
      let errorMessage = 'Erro ao salvar dados da empresa'

      // Erro de CORS ou Network
      if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        errorMessage = 'Erro de conexão com o servidor. Verifique sua conexão ou tente novamente mais tarde.'
      }
      // Erro da API com response
      else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.status === 401) {
        errorMessage = 'Não autorizado. Por favor, tente novamente.'
      } else if (error?.response?.status === 403) {
        errorMessage = 'Acesso negado. Verifique as permissões.'
      } else if (error?.response?.status === 404) {
        errorMessage = 'Endpoint não encontrado. Verifique a configuração da API.'
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.'
      }
      // Erro com mensagem direta
      else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      // Erro vazio ou desconhecido
      else if (error && typeof error === 'object') {
        // Tentar serializar o erro
        try {
          const errorStr = JSON.stringify(error, Object.getOwnPropertyNames(error))
          if (errorStr !== '{}') {
            errorMessage = `Erro: ${errorStr}`
          }
        } catch {
          errorMessage = 'Erro desconhecido ao processar a requisição'
        }
      }

      // Formatar mensagem para melhor legibilidade
      errorMessage = String(errorMessage || 'Erro desconhecido')
        .replace(/([A-Z])/g, ' $1') // Adicionar espaço antes de maiúsculas
        .replace(/^./, str => str.toUpperCase()) // Primeira letra maiúscula
        .replace(/\s+/g, ' ') // Remover espaços duplos
        .trim()

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmRecovery = async () => {
    if (!pendingOrder) return

    try {
      console.log('🔄 [CompanyForm] Carregando dados do pedido pendente para retomar...')

      // Buscar detalhes completos do pedido
      const pedido = await PedidosService.getPedidoById(pendingOrder.id)
      console.log('📋 [CompanyForm] Dados completos do pedido:', pedido)

      // 1. Mapear e salvar companyData
      const companyData = {
        ocupation: pedido.empresa.cnpj && pedido.empresa.cnpj.length > 11 ? 'PJ' : 'PF',
        cnpj: pedido.empresa.cnpj || '',
        cpf: pedido.empresa.cnpj || '',
        nomeEmpresa: pedido.empresa.razao_social,
        nomeEmpregador: pedido.empresa_solicitante?.nome || '',
        email: pedido.empresa_solicitante?.email || '',
        telefone: pedido.empresa.telefone || '',
        pcmso: '0',
        nomeMedico: '',
        crmMedico: '',
        ufCRM: '',
      }
      localStorage.setItem('companyData', JSON.stringify(companyData))
      console.log('✅ [CompanyForm] companyData salvo')

      // 2. Mapear colaboradores do pedido e exames selecionados
      const employeesMap = new Map<number, any>()
      const examsSet = new Set<number>()

      pedido.agendamentos?.forEach((ag: any) => {
        if (!employeesMap.has(ag.colaborador_id)) {
          employeesMap.set(ag.colaborador_id, {
            id: ag.colaborador.id.toString(),
            nome: ag.colaborador.nome,
            cpf: ag.colaborador.cpf,
            clinica: ag.clinica_id.toString(),
            clinicaNome: ag.clinica.nome,
            dataAgendamento: ag.data_agendamento?.split('T')[0] || '',
            examesSelecionados: [],
            variacao: ag.variacao_id ? ag.variacao_id.toString() : '1',
            exame: ag.exame?.id ? ag.exame.id.toString() : '1', // Tipo de exame (Periódico, Admissional, etc)
          })
        }
        const emp = employeesMap.get(ag.colaborador_id)
        // Adicionar exame clínico selecionado
        if (ag.exame_clinico?.id) {
          emp.examesSelecionados.push(ag.exame_clinico.id)
          examsSet.add(ag.exame_clinico.id)
        }
      })

      const employeesData = Array.from(employeesMap.values())

      // Vincular colaboradores à empresa atual antes de salvar
      try {
        const empresaDataStr = localStorage.getItem('empresaData')
        if (empresaDataStr) {
          const empresaData = JSON.parse(empresaDataStr)
          const employeesDataWithCompany = employeesData.map((emp: any) => ({
            ...emp,
            empresa_id: empresaData.id,
            empresa_cnpj: empresaData.cnpj || empresaData.cpf,
          }))
          localStorage.setItem('employeesData', JSON.stringify(employeesDataWithCompany))
          console.log('✅ [CompanyForm] employeesData salvo com', employeesDataWithCompany.length, 'colaboradores')
        } else {
          localStorage.setItem('employeesData', JSON.stringify(employeesData))
          console.log('✅ [CompanyForm] employeesData salvo com', employeesData.length, 'colaboradores')
        }
      } catch (error) {
        console.warn('⚠️ [CompanyForm] Erro ao vincular empresa_id aos colaboradores:', error)
        localStorage.setItem('employeesData', JSON.stringify(employeesData))
      }

      // 3. Salvar examsData (exames selecionados)
      const examsData = Array.from(examsSet)
      localStorage.setItem('examsData', JSON.stringify(examsData))
      console.log('✅ [CompanyForm] examsData salvo com', examsData.length, 'exames')

      // 4. Salvar stepData (dados do fluxo)
      const stepData = {
        step: 3, // Resumo (já tem colaboradores e exames)
        companyData,
        employeesData: employeesData,
        examsData,
        orderData: null, // Será preenchido no resumo
      }
      localStorage.setItem('stepData', JSON.stringify(stepData))
      console.log('✅ [CompanyForm] stepData salvo')

      // 5. Salvar dados do pedido
      localStorage.setItem('pedidoId', pedido.id.toString())
      localStorage.setItem('pedidoData', JSON.stringify(pedido))

      const orderData = {
        pedido: pedido,
        valorTotal: Number(pedido.valor_total) || 0,
      }
      localStorage.setItem('orderData', JSON.stringify(orderData))
      console.log('✅ [CompanyForm] Dados do pedido salvos (pedidoId, pedidoData, orderData)')

      console.log('✅ [CompanyForm] Todos os dados do pedido pendente foram carregados. Redirecionando para resumo...')

      // Redirecionar para resumo
      router.push('/resumo')
    } catch (error) {
      console.error('❌ [CompanyForm] Erro ao recuperar pedido:', error)
      alert('Erro ao recuperar o pedido. Por favor, comece novamente.')
      setShowRecoveryModal(false)
    }
  }

  const handleCancelRecovery = () => {
    console.log('🔄 [CompanyForm] Usuário escolheu não retomar pedido. Limpando dados...')
    setShowRecoveryModal(false)
    setPendingOrder(null)
    // Limpar dados de pedido, colaboradores e agendamento (preservar dados da empresa)
    clearPedidoAndColaboradoresData()
    console.log('✅ [CompanyForm] Dados limpos. Continuando com novo pedido...')
    localStorage.removeItem('examsData')
    // Continuar fluxo normal
    router.push('/validar')
  }

  const handleContinueAfterSubscriptionCheck = (response: any) => {
    console.log('✅ Continuando após verificação de assinatura:', response)

    // Salvar dados da empresa no localStorage se ainda não estiver salvo
    if (response && !localStorage.getItem('empresaData')) {
      localStorage.setItem('empresaData', JSON.stringify(response))
    }

    // Chamar callback onSuccess se fornecido
    if (onSuccess) {
      onSuccess(response)
      return; // Interrompe o redirecionamento hardcoded se o fallback custom assumir
    }

    // Chamar callback onSubmit se fornecido
    if (onSubmit) {
      onSubmit(response)
    }

    // Redirecionar para resumo
    router.push('/resumo')
  }

  return (
    <div className={isModal ? "w-full" : "bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 max-w-4xl mx-auto"}>
      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Dados para agendamento
          </h3>
          <p className="text-gray-500 text-sm mb-6">Preencha as informações da empresa ou pessoa física responsável pelo faturamento.</p>
          <hr className="border-gray-200" />
        </div>

        {/* Seção 1: Identificação */}
        <div className="bg-gray-50/50 p-6 rounded-xl border border-gray-100 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">

          {/* Tipo de pessoa - Toggle Modernizado */}
          <div className="md:col-span-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Tipo de Cadastro
            </label>
            <div className="flex bg-gray-100/80 p-1.5 rounded-lg border border-gray-200/60 shadow-inner w-full">
              <button
                type="button"
                onClick={() => handleOcupationChange('PJ')}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${formData.ocupation === 'PJ'
                  ? 'bg-white text-green-700 shadow-sm border border-gray-200 ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                  }`}
              >
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                CNPJ
              </button>
              <button
                type="button"
                onClick={() => handleOcupationChange('PF')}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${formData.ocupation === 'PF'
                  ? 'bg-white text-green-700 shadow-sm border border-gray-200 ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                  }`}
              >
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                CPF
              </button>
            </div>
          </div>

          {/* Campo CNPJ/CPF */}
          <div className="md:col-span-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Número do Documento ({formData.ocupation === 'PJ' ? 'CNPJ' : 'CPF'})
            </label>
            <div className="relative">
              <input
                type="text"
                name={formData.ocupation === 'PJ' ? 'cnpj' : 'cpf'}
                value={formData.ocupation === 'PJ' ? formData.cnpj : formData.cpf}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors ${(formData.ocupation === 'PJ' && cnpjError) || (formData.ocupation === 'PF' && cpfError)
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 hover:border-gray-300'
                  } ${formData.ocupation === 'PJ' && loadingCnpj ? 'pr-12' : ''}`}
                placeholder={formData.ocupation === 'PJ' ? 'Digite os 14 dígitos numéricos...' : 'Digite os 11 dígitos numéricos...'}
                required
              />
              {formData.ocupation === 'PJ' && loadingCnpj && (
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <svg className="animate-spin h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
            {(formData.ocupation === 'PJ' && cnpjError) && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>{cnpjError}</p>
            )}
            {(formData.ocupation === 'PF' && cpfError) && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>{cpfError}</p>
            )}
            {formData.ocupation === 'PJ' && (
              <div className="mt-4 bg-green-50/30 p-3 rounded-lg border border-green-100/50">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="optante_simples_nacional"
                    checked={formData.optante_simples_nacional}
                    disabled={hasSimplesNacionalFromApi}
                    onChange={(e) => setFormData(prev => ({ ...prev, optante_simples_nacional: e.target.checked }))}
                    className="peer w-5 h-5 text-green-600 border-gray-300 rounded shadow-sm focus:ring-green-500/20 focus:ring-4 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 font-semibold select-none group-hover:text-gray-900 transition-colors flex items-center gap-1.5">
                    Sua empresa é Optante pelo Simples Nacional?
                    {hasSimplesNacionalFromApi && (
                      <span className="text-[10px] font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                        Definido via Receita Federal
                      </span>
                    )}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Seção 2: Dados Principais (Grid 2 Colunas) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Razão Social */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {formData.ocupation === 'PJ' ? 'Razão Social' : 'Nome Completo'}
            </label>
            <input
              type="text"
              name="nomeEmpresa"
              value={formData.nomeEmpresa}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors hover:border-gray-300"
              placeholder={formData.ocupation === 'PJ' ? 'Ex: Tech Solutions SA' : 'Ex: João da Silva'}
              required
            />
          </div>

          {/* Nome para Contato */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nome para Contato
            </label>
            <input
              type="text"
              name="nomeEmpregador"
              value={formData.nomeEmpregador}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors hover:border-gray-300"
              placeholder="Com quem iremos falar?"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              E-mail Comercial
            </label>
            <input
              ref={emailInputRef}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors hover:border-gray-300"
              placeholder="exemplo@gmail.com"
              required
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Telefone / WhatsApp
            </label>
            <input
              type="tel"
              name="telefone"
              value={formData.telefone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors hover:border-gray-300"
              placeholder="(11) 99999-9999"
              required
            />
          </div>
        </div>


        {/* Divider Setup Clínico */}
        <div className="pt-6 border-t border-gray-100">
          <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
            Informações Clínicas
          </h4>

          {/* PCMSO */}
          <div className="bg-green-50/50 rounded-xl border border-green-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="text-gray-800 font-semibold mb-1">Deseja apontar o médico do PCMSO no ASO?</div>
              <p className="text-sm text-gray-500">Programa de Controle Médico de Saúde Ocupacional</p>
            </div>

            <div className="flex gap-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="pcmso"
                  value="1"
                  checked={formData.pcmso === '1'}
                  onChange={(e) => handlePCMSOChange(e.target.value)}
                  className="sr-only"
                />
                <span className={`px-8 py-2.5 rounded-lg border font-semibold transition-all shadow-sm ${formData.pcmso === '1'
                  ? 'bg-green-600 text-white border-green-600 ring-2 ring-green-600/20'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50/30'
                  }`}>
                  Sim
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="pcmso"
                  value="0"
                  checked={formData.pcmso !== '1'}
                  onChange={(e) => handlePCMSOChange(e.target.value)}
                  className="sr-only"
                />
                <span className={`px-8 py-2.5 rounded-lg border font-semibold transition-all shadow-sm ${formData.pcmso !== '1'
                  ? 'bg-gray-800 text-white border-gray-800 ring-2 ring-gray-800/20'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                  Não
                </span>
              </label>
            </div>
          </div>

          {/* Campos PCMSO */}
          {formData.pcmso === '1' && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="md:col-span-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome do Médico Responsável
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                  </div>
                  <input
                    type="text"
                    name="nomeMedico"
                    value={formData.nomeMedico}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors"
                    placeholder="Nome completo do médico"
                    maxLength={255}
                  />
                </div>
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  N° CRM
                </label>
                <input
                  type="number"
                  name="crmMedico"
                  value={formData.crmMedico}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors"
                  placeholder="Apenas números"
                  maxLength={10}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  UF
                </label>
                <select
                  name="ufCRM"
                  value={formData.ufCRM}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white text-gray-900 transition-colors appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 0.5rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em` }}
                >
                  <option value="">UF...</option>
                  <option value="AC">AC</option><option value="AL">AL</option><option value="AP">AP</option><option value="AM">AM</option><option value="BA">BA</option><option value="CE">CE</option><option value="DF">DF</option><option value="ES">ES</option><option value="GO">GO</option><option value="MA">MA</option><option value="MT">MT</option><option value="MS">MS</option><option value="MG">MG</option><option value="PA">PA</option><option value="PB">PB</option><option value="PR">PR</option><option value="PE">PE</option><option value="PI">PI</option><option value="RJ">RJ</option><option value="RN">RN</option><option value="RS">RS</option><option value="RO">RO</option><option value="RR">RR</option><option value="SC">SC</option><option value="SP">SP</option><option value="SE">SE</option><option value="TO">TO</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <FormMessage
            type="error"
            message={error}
            onClose={() => setError('')}
          />
        )}

        {/* Error Loading Termos */}
        {!termosCondicoes && !loadingTermos && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            Não foi possível carregar os Termos e Condições. Por favor, recarregue a página.
          </div>
        )}

        {/* Footer actions */}
        <div className="pt-8 mt-2 border-t border-gray-100 flex items-center justify-between flex-wrap gap-6">
          {/* Termos e Condições Toggle */}
          {termosCondicoes && (
            <div className="flex-1 min-w-[280px]">
              <label className="flex items-center gap-3 cursor-pointer group bg-gray-50/50 hover:bg-gray-50 p-3 rounded-lg border border-transparent hover:border-gray-200 transition-all">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={termosAceitos}
                    onChange={(e) => handleToggleTermos(e.target.checked)}
                    className="peer w-6 h-6 text-green-600 border-gray-300 rounded shadow-sm focus:ring-green-500/20 focus:ring-4 cursor-pointer"
                  />
                </div>
                <span className="text-sm text-gray-700 font-medium">
                  Li e aceito os{' '}
                  <button
                    type="button"
                    onClick={handleOpenTermosModal}
                    className="text-green-600 hover:text-green-700 underline decoration-green-200 hover:decoration-green-500 transition-colors"
                  >
                    Termos e Condições de Serviço
                  </button>
                </span>
              </label>
            </div>
          )}

          {/* Submit Button */}
          <div className="w-full md:w-auto">
            <button
              type="submit"
              disabled={!!(loading || !termosCondicoes || !termosAceitos)}
              className="w-full md:w-auto bg-green-600 text-white px-8 py-3.5 rounded-xl hover:bg-green-500 transition-all shadow-md hover:shadow-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Salvando os Dados...' : 'Continuar Agendamento'}
              {!loading && (
                <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Recovery Modal */}
      <RecoveryModal
        isOpen={showRecoveryModal}
        onConfirm={handleConfirmRecovery}
        onCancel={handleCancelRecovery}
        orderDate={pendingOrder ? new Date(pendingOrder.created_at).toLocaleDateString('pt-BR') : ''}
        orderValue={pendingOrder ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pendingOrder.valor) : ''}
      />

      {/* Termos e Condições Modal */}
      {termosCondicoes && (
        <TermosCondicoesModal
          isOpen={showTermosModal}
          onClose={handleCloseTermosModal}
          titulo={termosCondicoes.titulo}
          conteudo={termosCondicoes.conteudo}
          onReadComplete={handleTermosReadComplete}
        />
      )}

      {/* Subscription Warning Modal */}
      <SubscriptionWarningModal
        isOpen={showSubscriptionModal}
        onContinue={() => {
          setShowSubscriptionModal(false)
          // Continuar com o fluxo normal (criar avulso)
          const empresaResponse = JSON.parse(localStorage.getItem('empresaData') || '{}')
          handleContinueAfterSubscriptionCheck(empresaResponse)
        }}
        onGoToPortal={() => {
          setShowSubscriptionModal(false)
          // Redirecionar para portal de clientes
          const customerPortalUrl = process.env.NEXT_PUBLIC_CUSTOMER_PORTAL_URL || process.env.NEXT_PUBLIC_CLIENT_BASE_URL || null
          if (!customerPortalUrl) {
            console.error('❌ [CompanyForm] URL do portal de clientes não configurada. Configure NEXT_PUBLIC_CUSTOMER_PORTAL_URL ou NEXT_PUBLIC_CLIENT_BASE_URL')
            return
          }
          window.location.href = customerPortalUrl
        }}
      />
    </div>
  )
}
